import { Chip, CircularProgress, Grid, Skeleton } from '@mui/material';
import { parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { TimeWindow } from '@nx-smartmonkey/shared/interfaces';
import { HSpacer, RoutalDialog, RoutalPalette, RoutalSearchInput, RoutalTooltip } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import DialogInfo from '../../../../assets/svg/DialogInfo';
import DialogWarning from '../../../../assets/svg/DialogWarning';
import WarningIcon from '../../../../assets/svg/WarningIcon';
import * as routes from '../../../../constants/Routing';
import { Vehicle } from '../../../../domain/vehicle/Vehicle';
import { useCreateDrivers } from '../../../../hooks/driver/useCreateDrivers';
import { useRetrieveActivePlanDrivers } from '../../../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActiveProjectResources } from '../../../../hooks/project/useRetrieveActiveProjectResources';
import { useSearchVehiclesEnabled } from '../../../../hooks/vehicle/useSearchVehiclesEnabled';
import Button from '../../../../react-components/Button';
import FormattedMessage from '../../../../react-components/FormattedCustomMessage';
import Text from '../../../../react-components/Text';
import { AlignmentTypes, DataTable } from '../../../../react-components/data-table/DataTable';
import eventsPanel from '../../../../services/events';
import { VehicleAvatar } from '../../../shared/VehicleAvatar';

type RoutesFromVehiclesDialogProps = {
  open: boolean;
  handleCloseDialog: (event?: any) => void;
  planId?: string;
};

export const DriversFromVehiclesDialog = ({ open, handleCloseDialog, planId }: RoutesFromVehiclesDialogProps) => {
  const navigate = useNavigate();
  const { data: projectResources } = useRetrieveActiveProjectResources();
  const { run: createDrivers } = useCreateDrivers();
  const { data: currentPlanDrivers } = useRetrieveActivePlanDrivers();
  const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([]);
  const [filterText, setFilterText] = useState<string | undefined>(undefined);
  const [vehicleOffset, setVehicleOffset] = useState<number>(0);
  const [vehicleLimit, setVehicleLimit] = useState<number>(100);

  const { isLoading: searchingVehicles, data: paginatedVehicles } = useSearchVehiclesEnabled({
    offset: vehicleOffset,
    limit: vehicleLimit,
    searchText: filterText,
  });

  useEffect(() => {
    if (open) {
      setVehicleOffset(0);
      setVehicleLimit(100);
      setSelectedVehicles([]);
      setFilterText(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getWarning = () => {
    let content;

    if (resultingQuota() < 0) {
      content = (
        <Text style={{ color: `white`, fontWeight: `bold` }}>
          <FormattedMessage
            tagName="span"
            id="plans.dialogs.routes_from_vehicles.quota_exceeded_warning"
            values={{
              maxVehicles: projectResources?.max_vehicles_plan ?? 0,
              surplus: Math.abs(resultingQuota()),
              a: (chunks: any) => (
                <span
                  style={{
                    cursor: `pointer`,
                    color: `white`,
                    fontWeight: `bold`,
                    textDecoration: `underline`,
                  }}
                  onClick={() => {
                    navigate(routes.SETTINGS.ORGANIZATION.PROJECTS);
                  }}
                >
                  {chunks}
                </span>
              ),
            }}
          />
        </Text>
      );
    } else if (hasAlreadyAddedVehicles()) {
      content = (
        <Text
          style={{ color: `white`, fontWeight: `bold` }}
          intlId="plans.dialogs.routes_from_vehicles.already_added_warning"
        />
      );
    }

    if (!content) return null;

    return (
      <Grid
        container
        justifyContent="flex-start"
        alignItems="center"
        wrap="nowrap"
        style={{
          width: `100%`,
          backgroundColor: RoutalPalette.primary40,
          padding: `8px 24px`,
        }}
      >
        <DialogWarning style={{ width: `24px`, height: `24px`, color: `white` }} />

        <HSpacer small />

        <Grid item container justifyContent="center">
          {content}
        </Grid>
      </Grid>
    );
  };

  const getInfo = () => (
    <Grid
      container
      justifyContent="flex-start"
      alignItems="center"
      wrap="nowrap"
      style={{
        width: `100%`,
        backgroundColor: `#393939`,
        padding: `8px 24px`,
      }}
    >
      <DialogInfo style={{ width: `24px`, height: `24px`, color: `white` }} />

      <HSpacer small />

      <Grid item container justifyContent="center">
        <Text style={{ color: `white`, fontWeight: `bold` }}>
          <FormattedMessage
            tagName="span"
            id={`plans.dialogs.routes_from_vehicles.info`}
            values={{
              a: (chunks: any) => (
                <span
                  style={{
                    cursor: `pointer`,
                    color: `white`,
                    fontWeight: `bold`,
                    textDecoration: `underline`,
                  }}
                  onClick={() => {
                    navigate(routes.DRIVERS.VEHICLES);
                  }}
                >
                  {chunks}
                </span>
              ),
            }}
          />
        </Text>
      </Grid>
    </Grid>
  );

  const resultingQuota = () =>
    (projectResources?.max_vehicles_plan ?? 0) - ((currentPlanDrivers ?? []).length + selectedVehicles.length);

  const handleCreateDrivers = () => {
    const newDrivers = selectedVehicles.map((vehicle: Vehicle) => vehicle.driverFromVehicle());

    createDrivers({ drivers: newDrivers, planId: planId! });
    eventsPanel.createDrivers(`Desde Vehículos`);
    handleCloseDialog();
  };

  const isVehicleAlreadyAdded = (vehicleId: string) => {
    const planDriversVehiclesIds = (currentPlanDrivers ?? []).filter((d) => !!d.vehicle_id).map((d) => d.vehicle_id);
    return planDriversVehiclesIds.includes(vehicleId);
  };

  const hasAlreadyAddedVehicles = () => {
    return selectedVehicles.some((v) => isVehicleAlreadyAdded(v.id));
  };

  const { docs, limit, page, pages, total } = paginatedVehicles || {};

  return (
    <RoutalDialog
      open={open}
      onCloseDialog={handleCloseDialog}
      dialogProps={{
        maxWidth: `lg`,
        disableEscapeKeyDown: true,
        disableBackdropClick: true,
      }}
      titleContent={<Text variant="h6" intlId="plans.dialogs.routes_from_vehicles.title" />}
      titleRightContent={
        <Grid container direction="row" style={{ marginRight: `12px` }}>
          <Grid item>
            <RoutalTooltip
              title={
                <Text
                  style={{ color: `white` }}
                  intlId={`plans.dialogs.routes_from_vehicles.add_tooltip${
                    selectedVehicles.length === 0 ? `_warning` : ``
                  }`}
                />
              }
            >
              <Button
                disabled={searchingVehicles || selectedVehicles.length === 0 || resultingQuota() < 0}
                style={{
                  minWidth: `125px`,
                }}
                onClick={() => {
                  handleCreateDrivers();
                }}
              >
                {searchingVehicles ? (
                  <CircularProgress size={32} />
                ) : (
                  <Text variant="button" intlId="add" style={{ color: `white` }} />
                )}
              </Button>
            </RoutalTooltip>
          </Grid>
        </Grid>
      }
      dialogContentWarning={getWarning() ?? getInfo()}
      showFooter={false}
    >
      <div style={{ height: `600px`, width: `100%` }}>
        <Grid container direction="column" style={{ height: `100%`, width: `100%` }} wrap="nowrap">
          <Grid container direction="row" alignItems="center" justifyContent="space-between" wrap="nowrap">
            <Grid
              item
              container
              alignItems="center"
              style={{
                minHeight: `46px`,
              }}
            >
              {searchingVehicles ? (
                <Skeleton variant="rectangular" animation="pulse" height={20} width={220} />
              ) : (
                <Text
                  variant="body"
                  intlId={`${
                    selectedVehicles.length > 0 ? `vehicles.header.selected_info` : `vehicles.header.num_rows`
                  }`}
                  intlValues={{
                    num: selectedVehicles.length,
                    total: total ?? ``,
                  }}
                />
              )}
            </Grid>

            <Grid container style={{ height: `100%`, width: `100%` }} justifyContent="flex-end">
              <Grid item>
                <Grid container direction="column" justifyContent="center" style={{ height: `100%` }}>
                  <RoutalSearchInput
                    inputContainerStyles={{
                      width: `260px`,
                    }}
                    onChange={_.debounce((text: any) => {
                      setFilterText(text);
                    }, 300)}
                    searchLive
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <DataTable
            loading={searchingVehicles}
            docs={(docs ?? [])
              .filter((vehicle) => vehicle.enabled)
              .map((vehicle) => ({
                doc: vehicle,
                checkboxIcon: { element: <div style={{ width: `24px` }} /> },
              }))}
            maxHeight="calc(600px - 44px)"
            total={total ?? 0}
            limit={limit}
            page={page}
            pages={pages}
            selectedIds={selectedVehicles.map((vehicle) => vehicle.id)}
            noFooter={(pages ?? 0) <= 1}
            emptyMessage={<Text intlId="no_results" />}
            onChangePage={({ offset, limit }) => {
              setVehicleOffset(offset);
              setVehicleLimit(limit);
            }}
            onMultipleSelection={({ selectedIds, fromGlobalCheckbox }) => {
              if (fromGlobalCheckbox) {
                if (selectedVehicles.length > 0) {
                  setSelectedVehicles([]);
                } else {
                  setSelectedVehicles(docs ?? []);
                }
              } else {
                const vehicles = docs?.filter((client) => (selectedIds ?? []).includes(client.id)) ?? [];
                vehicles.push(
                  ...selectedVehicles.filter(
                    (selectedVehicle) => !(docs ?? []).map((docVehicle) => docVehicle.id).includes(selectedVehicle.id)
                  )
                );
                setSelectedVehicles(_.uniqBy(vehicles, `id`));
              }
            }}
            onRowDoubleClick={(vehicle) => {
              const selectedVehicle = docs?.find((vehicleRow) => vehicleRow.id === vehicle.id);
              if (!selectedVehicle) return;
              if (selectedVehicles.some((v) => v.id === selectedVehicle.id)) {
                setSelectedVehicles(selectedVehicles.filter((v) => v.id !== selectedVehicle.id));
              } else {
                setSelectedVehicles(_.uniqBy([...selectedVehicles, selectedVehicle], `id`));
              }
            }}
            columns={[
              {
                accessorName: `label`,
                name: <Text variant="label" intlId="vehicles.data.details.label" />,
                width: `300px`,
                formatter: ({ doc }: { doc: Vehicle }) => (
                  <Grid container direction="row" style={{ height: `48px` }} alignItems="center">
                    <Grid item>
                      <VehicleAvatar vehicleId={doc.id} vehicleColor={doc.color?.value} />
                    </Grid>

                    <HSpacer small />

                    <Grid item>
                      {doc.label && doc.label !== `` ? (
                        <RoutalTooltip title={doc.label} key="label">
                          <Text variant="body">
                            {doc.label.length > 40 ? `${doc.label.substring(0, 40)}...` : `${doc.label}`}
                          </Text>
                        </RoutalTooltip>
                      ) : (
                        <Text variant="body" style={{ color: RoutalPalette.secondary00 }} intlId="no_name" />
                      )}
                    </Grid>
                  </Grid>
                ),
              },
              {
                accessorName: `default_start_location`,
                name: <Text variant="label" intlId="vehicles.data.details.default_start_location" />,
                width: `210px`,
                formatter: ({ doc }: { doc: Vehicle }) =>
                  doc.default_start_location ? (
                    <RoutalTooltip title={doc.default_start_location?.label ?? ``} key="default_start_location">
                      <Text
                        variant="body"
                        style={{
                          maxWidth: `210px`,
                          textOverflow: `ellipsis`,
                          overflow: `hidden`,
                          whiteSpace: `nowrap`,
                        }}
                      >
                        {doc.default_start_location.label}
                      </Text>
                    </RoutalTooltip>
                  ) : (
                    `-`
                  ),
              },
              {
                accessorName: `default_time_window`,
                align: `center` as AlignmentTypes,
                name: <Text variant="label" intlId="vehicles.data.details.default_time_window" />,
                formatter: ({ value: defaultTimeWindow }: { value: TimeWindow }) => {
                  if (defaultTimeWindow && defaultTimeWindow.length > 0) {
                    return (
                      <Chip
                        key={defaultTimeWindow[0] + defaultTimeWindow[1]}
                        label={
                          <Text variant="body" style={{ cursor: `pointer` }}>{`${parseSecondsToHM(
                            defaultTimeWindow[0]
                          )}-${parseSecondsToHM(defaultTimeWindow[1])}`}</Text>
                        }
                      />
                    );
                  }
                  return null;
                },
              },
              {
                accessorName: `phone`,
                width: 110,
                name: <Text variant="label" intlId="vehicles.data.details.phone" />,
              },
              {
                accessorName: `warnings`,
                width: 68,
                align: `center` as AlignmentTypes,
                formatter: ({ doc }: { doc: Vehicle }) => {
                  if (!isVehicleAlreadyAdded(doc.id)) {
                    return null;
                  }

                  return (
                    <RoutalTooltip
                      title={
                        <Text
                          style={{ color: `white` }}
                          intlId="plans.dialogs.routes_from_vehicles.vehicle_already_added"
                        />
                      }
                    >
                      <div style={{ width: `24px`, height: `24px` }}>
                        <WarningIcon style={{ color: RoutalPalette.primary40 }} />
                      </div>
                    </RoutalTooltip>
                  );
                },
              },
            ]}
          />
        </Grid>
      </div>
    </RoutalDialog>
  );
};
